import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';
import {
  TrendUp, Lightning, Sparkle, Trophy, Warning, CheckCircle, Target,
  ArrowRight, Lock, EnvelopeSimple, DownloadSimple,
} from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';
import Logo from '@/components/Logo';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import useDocumentMeta from '@/hooks/useDocumentMeta';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scoreColor = (s) =>
  s >= 80 ? '#16A34A' : s >= 60 ? '#0F1B36' : s >= 40 ? '#CA8A04' : '#E60000';

export default function DemoReportPage() {
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const isTR = language === 'tr';

  useDocumentMeta({
    title: isTR ? 'Örnek Stratejik Rapor — GrowthRIQ' : 'Sample Strategic Report — GrowthRIQ',
    description: isTR
      ? 'GrowthRIQ stratejik dijital olgunluk raporunu kayıt olmadan inceleyin.'
      : 'Explore a GrowthRIQ strategic digital maturity report without signing up.',
    canonical: isTR ? '/demo' : '/en/demo',
    alternates: { tr: '/demo', en: '/en/demo' },
    ogKind: 'landing',
  });
  useEffect(() => {
    // Mark page as noindex (sample data, no SEO value)
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    const prev = el.getAttribute('content');
    el.setAttribute('content', 'noindex,nofollow');
    return () => {
      if (prev) el.setAttribute('content', prev);
      else el.remove();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/demo/report?lang=${language}`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [language]);

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#3CD9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const r = data.full_report_json || {};
  const overall = r.overall_score ?? 0;
  const categoryScores = r.category_scores || {};
  const swot = r.swot_analysis || {};
  const roadmap = r.roadmap || {};

  const radarData = Object.entries(categoryScores).map(([name, score]) => ({
    category: name.split(' ')[0],
    fullName: name,
    score,
    fullMark: 100,
  }));
  const barData = Object.entries(categoryScores)
    .map(([name, score]) => ({
      name: name.length > 18 ? `${name.substring(0, 18)}…` : name,
      fullName: name,
      score,
    }))
    .sort((a, b) => b.score - a.score);

  const ctaPrimary = isTR ? 'Kayıt ol & kendi raporunu üret' : 'Sign up & generate your own report';
  const ctaSecondary = isTR ? 'Ücretsiz araçları dene' : 'Try the free tools';

  return (
    <div className="min-h-screen bg-white text-[#0F1B36]" data-testid="public-demo-report">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E4E4E7]">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="demo-logo">
            <Logo variant="light" size="md" />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to="/register"
              data-testid="demo-header-cta"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#0F1B36] text-white text-xs font-bold hover:opacity-90"
            >
              {isTR ? 'Ücretsiz başla' : 'Start free'} <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* Demo banner */}
      <div className="bg-[#FEF3C7] text-[#92400E] text-xs sm:text-sm text-center px-6 py-2 font-medium">
        <Lightning size={14} weight="fill" className="inline-block mr-1.5 -mt-0.5" />
        {isTR
          ? 'Bu bir örnek rapordur. Kendi şirketiniz için ücretsiz kayıt olarak gerçek raporunuzu 5 dakikada alın.'
          : "This is a sample report. Sign up for free to get your real report for your company in 5 minutes."}
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-12">
        <p className="text-xs uppercase tracking-widest text-[#52525B] font-semibold mb-3">
          {isTR ? 'Örnek Stratejik Rapor' : 'Sample Strategic Report'}
        </p>
        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-3">
          {data.company_name}
        </h1>
        <p className="text-[#52525B] max-w-2xl text-sm sm:text-base">
          {isTR
            ? 'Aşağıda, GrowthRIQ değerlendirmesini tamamlayan bir e-ticaret şirketinin gerçek formatta üretilen stratejik raporu yer almaktadır.'
            : 'Below is the strategic report produced for a real e-commerce company after completing the GrowthRIQ assessment.'}
        </p>
      </section>

      {/* Overall score card */}
      <section className="max-w-6xl mx-auto px-6 mb-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1 bg-[#0F1B36] text-white rounded-2xl p-7 flex flex-col justify-between">
            <div className="text-xs uppercase tracking-wider text-white/60 font-semibold">
              {isTR ? 'Genel Olgunluk Skoru' : 'Overall Maturity Score'}
            </div>
            <div className="flex items-end gap-2 my-4">
              <div className="text-6xl font-bold leading-none" data-testid="demo-overall-score">{overall.toFixed(1)}</div>
              <div className="text-xl text-white/50 pb-1">/100</div>
            </div>
            <div className="text-sm text-[#3CD9B8] font-semibold inline-flex items-center gap-1">
              <TrendUp size={14} weight="fill" /> {isTR ? 'Gelişmekte Olan' : 'Developing'}
            </div>
          </div>

          <div className="md:col-span-2 bg-[#F4F4F5] rounded-2xl p-7">
            <div className="text-xs uppercase tracking-wider text-[#52525B] font-semibold mb-3">
              {isTR ? 'Yönetici Özeti' : 'Executive Summary'}
            </div>
            <div className="prose prose-sm max-w-none text-[#0F1B36]/90 leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {data.executive_summary || ''}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </section>

      {/* Charts */}
      <section className="max-w-6xl mx-auto px-6 mb-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#E4E4E7] p-6">
          <h2 className="font-bold text-base mb-4">{isTR ? 'Kategori Bazlı Skor (Radar)' : 'Category Scores (Radar)'}</h2>
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#E4E4E7" />
              <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#52525B' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar dataKey="score" stroke="#3CD9B8" fill="#3CD9B8" fillOpacity={0.35} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border border-[#E4E4E7] p-6">
          <h2 className="font-bold text-base mb-4">{isTR ? 'Sıralı Skorlar' : 'Ranked Scores'}</h2>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={barData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
              <Tooltip />
              <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                {barData.map((d) => (
                  <Cell key={d.fullName} fill={scoreColor(d.score)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Mid-page CTA */}
      <section className="max-w-6xl mx-auto px-6 mb-10">
        <div className="rounded-2xl bg-gradient-to-r from-[#0F1B36] to-[#1E293B] text-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-[#3CD9B8] font-semibold mb-2">
              {isTR ? 'Etkilendiniz mi?' : 'Impressed?'}
            </div>
            <h3 className="text-xl sm:text-2xl font-bold mb-1">
              {isTR ? 'Kendi raporunuzu 5 dakikada üretin.' : 'Generate your own report in 5 minutes.'}
            </h3>
            <p className="text-white/70 text-sm">
              {isTR
                ? 'Kayıt ücretsiz. Kredi kartı gerekmez. İlk değerlendirme & rapor sizden.'
                : 'Free to sign up. No credit card required. Your first assessment & report on us.'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0 w-full sm:w-auto">
            <Link
              to="/register"
              data-testid="demo-midpage-cta"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#3CD9B8] text-[#0F1B36] font-bold text-sm hover:opacity-90"
            >
              <Sparkle size={16} weight="fill" /> {ctaPrimary}
            </Link>
            <Link
              to={isTR ? '/araclar' : '/tools'}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-white/20 text-white font-semibold text-sm hover:bg-white/5"
            >
              {ctaSecondary}
            </Link>
          </div>
        </div>
      </section>

      {/* SWOT */}
      <section className="max-w-6xl mx-auto px-6 mb-10">
        <h2 className="text-xl sm:text-2xl font-bold mb-6">{isTR ? 'SWOT Analizi' : 'SWOT Analysis'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <SwotCard color="#16A34A" icon={Trophy} title={isTR ? 'Güçlü Yönler' : 'Strengths'} items={swot.strengths} />
          <SwotCard color="#CA8A04" icon={Warning} title={isTR ? 'Zayıf Yönler' : 'Weaknesses'} items={swot.weaknesses} />
          <SwotCard color="#0EA5E9" icon={Lightning} title={isTR ? 'Fırsatlar' : 'Opportunities'} items={swot.opportunities} />
          <SwotCard color="#DC2626" icon={Target} title={isTR ? 'Tehditler' : 'Threats'} items={swot.threats} />
        </div>
      </section>

      {/* Roadmap */}
      <section className="max-w-6xl mx-auto px-6 mb-10">
        <h2 className="text-xl sm:text-2xl font-bold mb-6">{isTR ? '30 / 60 / 90 Günlük Yol Haritası' : '30 / 60 / 90 Day Roadmap'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <RoadmapCard label={isTR ? 'İlk 30 Gün' : 'First 30 Days'} items={roadmap.immediate} accent="#3CD9B8" />
          <RoadmapCard label={isTR ? '60 Gün' : '60 Days'} items={roadmap.short_term} accent="#0EA5E9" />
          <RoadmapCard label={isTR ? '90 Gün' : '90 Days'} items={roadmap.medium_term} accent="#0F1B36" />
        </div>
      </section>

      {/* Locked premium section teaser */}
      <section className="max-w-6xl mx-auto px-6 mb-12">
        <div className="rounded-2xl border-2 border-dashed border-[#E4E4E7] p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/95 pointer-events-none" />
          <Lock size={28} weight="duotone" className="mx-auto text-[#52525B] mb-3" />
          <h3 className="font-bold text-lg mb-1">
            {isTR ? 'Kategori bazlı detaylı içgörüler kilitli' : 'Detailed category-level insights are locked'}
          </h3>
          <p className="text-sm text-[#52525B] max-w-xl mx-auto mb-5">
            {isTR
              ? '10 kategori için ayrı ayrı bulgular, kıyaslamalar ve aksiyon adımları gerçek raporda yer alır. Kayıt olun ve kendi raporunuzu üretin.'
              : 'Per-category findings, benchmarks and action steps are included in the real report. Sign up to generate yours.'}
          </p>
          <Link
            to="/register"
            data-testid="demo-locked-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#0F1B36] text-white font-bold text-sm hover:opacity-90"
          >
            <Sparkle size={14} weight="fill" /> {ctaPrimary}
          </Link>
        </div>
      </section>

      {/* PDF email capture form */}
      <DemoEmailForm isTR={isTR} language={language} />

      {/* Footer mini-CTA */}
      <section className="bg-[#F4F4F5] py-12 px-6 border-t border-[#E4E4E7]">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight">
            {isTR ? 'Şimdi kendi raporunuza geçin.' : 'Now get your own report.'}
          </h3>
          <p className="text-[#52525B] mb-6">
            {isTR
              ? 'Şirketinizin gerçek skoru, gerçek SWOT, gerçek yol haritası.'
              : 'Your company’s real score, real SWOT, real roadmap.'}
          </p>
          <Link
            to="/register"
            data-testid="demo-footer-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-[#3CD9B8] text-[#0F1B36] font-bold hover:opacity-90"
          >
            <Sparkle size={16} weight="fill" /> {ctaPrimary}
          </Link>
        </div>
      </section>
    </div>
  );
}

function SwotCard({ icon: Icon, title, items = [], color }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="bg-white rounded-2xl border border-[#E4E4E7] p-5">
      <div className="flex items-center gap-2 mb-3">
        <span
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg"
          style={{ backgroundColor: `${color}15`, color }}
        >
          <Icon size={18} weight="fill" />
        </span>
        <h3 className="font-bold text-sm">{title}</h3>
        <span className="ml-auto text-xs text-[#52525B]">{list.length}</span>
      </div>
      <ul className="space-y-2.5">
        {list.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#0F1B36]/85">
            <CheckCircle size={14} weight="fill" className="text-[#3CD9B8] mt-1 shrink-0" />
            <span className="leading-snug">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RoadmapCard({ label, items = [], accent }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="bg-white rounded-2xl border border-[#E4E4E7] p-5">
      <div className="text-xs uppercase tracking-wider font-bold mb-3" style={{ color: accent }}>
        {label}
      </div>
      <ul className="space-y-3">
        {list.map((it, i) => (
          <li key={i} className="text-sm">
            <p className="font-semibold text-[#0F1B36] leading-snug">{it.title || it}</p>
            {(it.impact || it.effort) && (
              <p className="text-xs text-[#52525B] mt-1">
                {it.impact && (
                  <span className="inline-block mr-2">
                    <strong>Etki:</strong> {it.impact}
                  </span>
                )}
                {it.effort && (
                  <span>
                    <strong>Efor:</strong> {it.effort}
                  </span>
                )}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DemoEmailForm({ isTR, language }) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/tools/leads`, {
        email: email.trim(),
        tool_slug: 'demo-report',
        result_snapshot: { lang: language },
        consent_marketing: consent,
      });
      setSent(true);
      toast.success(
        isTR
          ? 'PDF indiriliyor — bir kopyası e-postanıza da gönderildi 🎉'
          : 'Downloading PDF — a copy is also on its way to your inbox 🎉',
      );
      // Also kick off an immediate PDF download for instant gratification
      const pdfUrl = `${API_URL}/demo/report.pdf?lang=${language}`;
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = isTR ? 'GrowthRIQ-Ornek-Rapor.pdf' : 'GrowthRIQ-Sample-Report.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      toast.error(
        isTR
          ? 'Gönderilemedi. Lütfen tekrar deneyin.'
          : 'Could not send. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-6xl mx-auto px-6 mb-12">
      <div className="rounded-2xl bg-white border-2 border-[#3CD9B8]/30 p-8 sm:p-10" data-testid="demo-email-form">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-2">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#3CD9B8]/15 text-[#3CD9B8] mb-4">
              <DownloadSimple size={22} weight="duotone" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2 tracking-tight">
              {isTR ? 'Bu raporu PDF olarak almak ister misiniz?' : 'Want this report as a PDF?'}
            </h3>
            <p className="text-sm text-[#52525B] leading-relaxed">
              {isTR
                ? 'E-posta adresinize anında PDF kopya gönderelim. Ofise dönüşünüzde, sunum hazırlığınızda veya ekibinizle paylaşırken işinize yarar.'
                : 'We will email you a PDF copy instantly. Handy when you get back to the office, prepare a deck or share with your team.'}
            </p>
          </div>

          {sent ? (
            <div className="lg:col-span-3 rounded-xl bg-[#3CD9B8]/10 border border-[#3CD9B8]/30 p-6 text-center">
              <CheckCircle size={36} weight="fill" className="text-[#3CD9B8] mx-auto mb-2" />
              <p className="font-semibold text-[#0F1B36]">
                {isTR ? 'PDF indiriliyor — e-postanıza da bir kopyası gönderildi.' : 'Downloading PDF — a copy was also sent to your inbox.'}
              </p>
              <p className="text-xs text-[#52525B] mt-1">
                {isTR ? 'Gelmediyse spam klasörünüzü kontrol edin.' : 'Check your spam folder if you cannot find the email.'}
              </p>
              <Link
                to="/register"
                data-testid="demo-email-form-cta-after"
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0F1B36] text-white font-bold text-sm hover:opacity-90"
              >
                <Sparkle size={14} weight="fill" />
                {isTR ? 'Kendi raporunu üret' : 'Generate your own report'}
              </Link>
            </div>
          ) : (
            <form className="lg:col-span-3 flex flex-col gap-3" onSubmit={submit} data-testid="demo-email-form-form">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <EnvelopeSimple size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                  <input
                    type="email"
                    required
                    placeholder={isTR ? 'sizin@firmaniz.com' : 'you@yourcompany.com'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="demo-email-input"
                    className="w-full pl-10 pr-3 py-3 rounded-md border border-[#E4E4E7] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3CD9B8] focus:border-transparent"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="demo-email-submit"
                  className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#0F1B36] text-white font-bold text-sm hover:opacity-90 disabled:opacity-50"
                >
                  <DownloadSimple size={16} weight="bold" />
                  {busy
                    ? isTR ? 'Gönderiliyor…' : 'Sending…'
                    : isTR ? 'PDF gönder' : 'Send PDF'}
                </button>
              </div>
              <label className="flex items-start gap-2 text-xs text-[#52525B] leading-snug select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="accent-[#3CD9B8] mt-0.5"
                  data-testid="demo-email-consent"
                />
                <span>
                  {isTR
                    ? 'GrowthRIQ’dan e-ticaret büyüme içerikleri ve fırsatlar almak istiyorum (haftada en fazla 1 e-posta, dilediğiniz an iptal edebilirsiniz).'
                    : 'I want to receive growth content & offers from GrowthRIQ (max 1 email/week, unsubscribe anytime).'}
                </span>
              </label>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

